import { useCallback, useMemo, useState } from 'react';
import { updateDocumentFile } from '../api/documents';
import type { DocumentMetadataState } from '../components/fileDetailPanel/DocumentMetadataSection';

// Manages document metadata state, validation flags, change detection, and save lifecycle.
export function useDocumentMetadataState(initialState: DocumentMetadataState) {
  const [metadataState, setMetadataState] = useState<DocumentMetadataState>(initialState);
  const [savedMetadataState, setSavedMetadataState] = useState<DocumentMetadataState>(initialState);
  const [hasErrors, setHasErrors] = useState(false);

  // Updates editable metadata together with the current validation status.
  const setMetadata = useCallback((state: DocumentMetadataState, hasError: boolean) => {
    setMetadataState(state);
    setHasErrors(hasError || state.selectedCompanyLevel === undefined || state.name === "" || state.selectedFolder === undefined);
  }, []);

  // Resets the metadata baseline and marks the reset state as already saved.
  const resetWith = useCallback((input: Partial<DocumentMetadataState>) => {
    setMetadataState((previous) => {
      const nextMetadataState = {
        ...previous,
        ...input,
      };

      setSavedMetadataState(nextMetadataState);
      setHasErrors(false);
      return nextMetadataState;
    });
  }, []);

  // Persists current metadata and synchronizes local editable and saved snapshots.
  const doSave = useCallback(
    async (fileId: string): Promise<DocumentMetadataState> => {
      const savePayload = {
        name: metadataState.name.trim(),
        category: Number(metadataState.category),
        companyLevel: metadataState.selectedCompanyLevel,
      };

      await updateDocumentFile(fileId, savePayload);

      const savedState: DocumentMetadataState = {
        ...metadataState,
        name: savePayload.name,
        category: String(savePayload.category),
      };

      setMetadataState(savedState);
      setSavedMetadataState(savedState);
      setHasErrors(false);

      return savedState;
    },
    [metadataState]
  );

  // Exposes derived form state for save-button and validation decisions.
  const metadataValueState: MetadataValueState = useMemo(
    () => ({
      hasErrors,
      hasChanges: hasMetadataChanges(metadataState, savedMetadataState),
    }),
    [hasErrors, metadataState, savedMetadataState]
  );

  return {
    metadataState,
    metadataValueState,
    setMetadata,
    doSave,
    resetWith,
  };
}

type MetadataValueState = {
  hasErrors: boolean;
  hasChanges: boolean;
};


// Normalizes metadata fields so comparisons and save payloads use consistent values.
function normalizeMetadataState(state: DocumentMetadataState): DocumentMetadataState {
  const normalizedName = state.name.trim();
  const normalizedCategory = String(Number(state.category));
  const normalizedFolder = state.selectedFolder ?? null;
  return {
    ...state,
    name: normalizedName,
    category: normalizedCategory,
    selectedFolder: normalizedFolder,
    selectedCompanyLevel: state.selectedCompanyLevel,
  };
}

// Compares editable metadata against the saved baseline to detect meaningful changes.
function hasMetadataChanges(
  currentState: DocumentMetadataState,
  savedState: DocumentMetadataState
): boolean {
  const normalizedCurrentState = normalizeMetadataState(currentState);
  const normalizedSavedState = normalizeMetadataState(savedState);
  return (
    normalizedCurrentState.name !== normalizedSavedState.name ||
    normalizedCurrentState.category !== normalizedSavedState.category ||
    normalizedCurrentState.selectedFolder !== normalizedSavedState.selectedFolder ||
    normalizedCurrentState.selectedCompanyLevel !== normalizedSavedState.selectedCompanyLevel
  );
}
